import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../api/axiosRest";
import { AUTH } from "../api/urls";
import { toast } from 'react-toastify';
import PageHeading from "../components/PageHeading";
import Spacing from "../components/Spacing";
import Div from "../components/Div";

export default function EmailVerificationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("verifying"); 
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Check for URL parameters first (from backend redirect)
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');
    const statusParam = urlParams.get('status');

    if (errorParam) {
      setStatus("error");
      switch (errorParam) {
        case 'expired':
          setMessage("Verification link has expired. Please request a new one.");
          break;
        case 'invalid':
          setMessage("Invalid verification link. Please check your email for the correct link.");
          break;
        default:
          setMessage("Verification failed. Please try again.");
      }
      return;
    }

    if (statusParam) {
      setStatus("success");
      switch (statusParam) {
        case 'success':
          setMessage("Email verified successfully! Your account has been activated.");
          toast.success("Email verified successfully! You can now login to your account.");
          break;
        case 'already_verified':
          setMessage("Your account is already verified. You can login to your account.");
          toast.info("Your account is already verified.");
          break;
        default:
          setMessage("Email verification completed.");
      }
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      return;
    }

    // If no URL parameters, try the API call (fallback for direct frontend access)
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${AUTH.VERIFY_EMAIL}${token}/`);
        setStatus("success");
        setMessage("Email verified successfully! Your account has been activated.");
        toast.success(response.data.message);
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (error) {
        setStatus("error");
        const errorMessage = error.response?.data?.error || "Verification failed. Please try again.";
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyEmail();
  }, [token, navigate, location.search]);

  return (
    <>
      <PageHeading
        title="Email Verification"
        bgSrc="/images/blog_hero_bg.jpeg"
        pageLinkText="Email Verification"
      />
      <Spacing lg="100" md="70" />
      <Div className="container">
        <Div className="row justify-content-center">
          <Div className="col-lg-6 text-center">
            {status === "verifying" && (
              <>
                <h3 className="cs-section_title">Verifying your email address...</h3>
                <div className="mt-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </>
            )}
            {status === "success" && (
              <>
                <h3 className="cs-section_title">Email Verified Successfully!</h3>
                <p>{message}</p>
                <p>You will be redirected to the login page in a few seconds.</p>
                <a href="/login" className="cs-btn cs-style1 cs-btn_lg">Go to Login</a>
              </>
            )}
            {status === "error" && (
              <>
                <h3 className="cs-section_title">Verification Failed</h3>
                <p>{message}</p>
                <div className="mt-4">
                  <a href="/resend-verification" className="cs-btn cs-style1 cs-btn_lg me-3">Resend Verification</a>
                  <a href="/login" className="cs-btn cs-style2 cs-btn_lg">Go to Login</a>
                </div>
              </>
            )}
          </Div>
        </Div>
      </Div>
      <Spacing lg="100" md="70" />
    </>
  );
}